import * as _ from 'lodash';

import { TableSettings } from './table-settings.model';
import { TableType, getEntityName } from 'src/shared/enums';
import { IBaseTable } from 'src/app/interfaces/calibration-set-table.interface';
import { IBaseTableCell } from 'src/app/interfaces/tables/cells/base-table-cell-interface';
import { ICalibrationSetModel } from 'src/app/interfaces/calibration-set-model.interface';
import { ITableFieldsConfiguration } from 'src/app/interfaces/tables/table-fields-configuration.interface';
import { ITableFieldDictionary } from 'src/app/interfaces/field-settings.interface';
import { ICalibrationSetValidationResult } from 'src/app/interfaces/zc-validation-result.interface';

export class BaseTable implements IBaseTable {
    settings: TableSettings;
    table: Array<IBaseTableCell<any>> = [];
    hasSections: boolean;
    calibrationType: TableType;

    constructor(settings?: ITableFieldsConfiguration, dictionary?: ITableFieldDictionary, hasSections?: boolean) {
        this.settings = new TableSettings(settings, dictionary, hasSections);
    }

    init(calibrationsSets: ICalibrationSetModel<any>[]) {
        if (this.isInitialized) {
            return;
        }
        const tableType = getEntityName(this.calibrationType);

        if (this.table.length || !_.get(calibrationsSets, 'length', 0)) {
            return;
        }
        calibrationsSets.forEach(calibrationSet => {
            (calibrationSet[tableType] as any).fieldsSettings = this.settings.fieldValidation;
            this.table.push({
                calibrationSet,
                value: calibrationSet[tableType]
            });
        });
        this.table.sort((a, b) =>
            a.calibrationSet.Name.toLowerCase() > b.calibrationSet.Name.toLowerCase()
                ? 1
                : b.calibrationSet.Name.toLowerCase() > a.calibrationSet.Name.toLowerCase()
                ? -1
                : 0
        );

        this.settings.detectGlobalChanges(this, true);
    }

    get isChanged(): boolean {
        return this.table.some(column => column.value.isChanged);
    }

    isValid(ignoreRequiredFields = false): boolean {
        const areThereErrors = this.table.some(column => {
            const calibrationSet = column.calibrationSet;
            const currentIgnoreRequiredFields =
                ignoreRequiredFields || (calibrationSet.isZoneCombination && !calibrationSet.validateRequiredFields);
            return !column.value.isValid(calibrationSet, currentIgnoreRequiredFields);
        });
        return !areThereErrors;
    }

    get isInitialized(): boolean {
        return !!_.get(this.table, 'length');
    }

    getChangedData() {
        return this.table.reduce((result, column) => {
            return column.value.isChanged ? result.concat(column.value.getChangedValues()) : result;
        }, []);
    }

    updateData(_data: any): boolean {
        // should be implemented in child class
        return false;
    }

    getValidationResults(
        ignoreRequiredFields: boolean = false
    ): { [property: number]: ICalibrationSetValidationResult } {
        const result: { [property: number]: ICalibrationSetValidationResult } = {};
        this.table.forEach(column => {
            const calibrationSet = column.calibrationSet;
            const currentIgnoreRequiredFields =
                ignoreRequiredFields || (calibrationSet.isZoneCombination && !calibrationSet.validateRequiredFields);
            result[column.calibrationSet.Id] = {
                ignoreRequiredFields: currentIgnoreRequiredFields,
                calibrationSetName: column.calibrationSet.Name,
                calibrationSet: column.calibrationSet,
                isValid: column.value.isValid(calibrationSet, currentIgnoreRequiredFields),
                validations: column.value.getValidationResults(calibrationSet, currentIgnoreRequiredFields)
            };
        });
        return result;
    }
}
